import { useState, useEffect } from 'react'
import { decryptAttachment, fetchMediaUrl, getMediaUrl } from '@/lib/utils/decrypt-attachment'
import { Lightbox } from '@/components/ui/Lightbox'
import { MapDialog } from '@/components/ui/MapDialog'
import { extractLocation } from '@/lib/utils/location'
import type { SerializedEvent } from '@/lib/matrix/events'

interface MediaMessageProps {
  event: SerializedEvent
}

function useMediaUrl(event: SerializedEvent): { url: string | null; loading: boolean } {
  const { url: rawUrl, encrypted, fileInfo } = getMediaUrl(event.content)
  const [url, setUrl] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!rawUrl) return

    setLoading(true)
    const promise = encrypted && fileInfo
      ? decryptAttachment(fileInfo)
      : fetchMediaUrl(rawUrl)

    promise
      .then(setUrl)
      .catch((err) => {
        console.warn('Failed to load media:', err)
        setUrl(null)
      })
      .finally(() => setLoading(false))
  }, [rawUrl, encrypted, fileInfo])

  return { url, loading }
}

function clampDimensions(w: number, h: number, maxW = 384, maxH = 320): { width: number; height: number } {
  const ratio = Math.min(maxW / w, maxH / h, 1)
  return { width: Math.round(w * ratio), height: Math.round(h * ratio) }
}

function ImageMessage({ event }: MediaMessageProps) {
  const { url, loading } = useMediaUrl(event)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const info = event.content.info as { w?: number; h?: number } | undefined
  const alt = (event.content.body as string) ?? 'Image'
  const dims = info?.w && info?.h ? clampDimensions(info.w, info.h) : { width: 192, height: 128 }

  return (
    <>
      <div
        className="rounded-md border border-[rgb(var(--np-border))] overflow-hidden bg-[rgb(var(--np-raised))]"
        style={{ width: dims.width, height: dims.height }}
      >
        {loading ? (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-xs text-[rgb(var(--np-tx-muted))]">Decrypting...</span>
          </div>
        ) : url ? (
          <button onClick={() => setLightboxOpen(true)} className="block w-full h-full cursor-zoom-in">
            <img
              src={url}
              alt={alt}
              className="w-full h-full object-cover hover:opacity-90 transition-opacity"
            />
          </button>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="text-xs text-[rgb(var(--np-tx-muted))] italic">Image unavailable</span>
          </div>
        )}
      </div>
      {lightboxOpen && url && (
        <Lightbox src={url} alt={alt} onClose={() => setLightboxOpen(false)} />
      )}
    </>
  )
}

// WhatsApp "GIFs" arrive as short MP4 clips. mautrix marks them with
// info["fi.mau.gif"] (or an image/gif mimetype); play them as a silent,
// looping, autoplaying clip rather than a click-to-play video player.
function isGif(event: SerializedEvent): boolean {
  const info = event.content.info as { mimetype?: string; ['fi.mau.gif']?: boolean } | undefined
  return info?.['fi.mau.gif'] === true || info?.mimetype === 'image/gif' || event.content['fi.mau.gif'] === true
}

// A video's poster frame lives in info.thumbnail_url (plaintext) or
// info.thumbnail_file (encrypted) — fetch it as a blob the same way as media.
function useVideoPoster(event: SerializedEvent): string | null {
  const info = event.content.info as
    | { thumbnail_url?: string; thumbnail_file?: Parameters<typeof decryptAttachment>[0] }
    | undefined
  const thumbFile = info?.thumbnail_file
  const thumbUrl = info?.thumbnail_url
  const [poster, setPoster] = useState<string | null>(null)

  useEffect(() => {
    const promise = thumbFile?.url ? decryptAttachment(thumbFile) : thumbUrl ? fetchMediaUrl(thumbUrl) : null
    if (!promise) return
    let active = true
    promise.then((u) => active && setPoster(u)).catch(() => {})
    return () => {
      active = false
    }
  }, [thumbUrl, thumbFile])

  return poster
}

function VideoMessage({ event }: MediaMessageProps) {
  const { url, loading } = useMediaUrl(event)
  const poster = useVideoPoster(event)
  const gif = isGif(event)

  if (loading) return <p className="text-sm text-[rgb(var(--np-tx-muted))] italic">Decrypting video...</p>
  if (!url) return <p className="text-sm text-[rgb(var(--np-tx-muted))] italic">Video unavailable</p>

  if (gif) {
    const info = event.content.info as { w?: number; h?: number } | undefined
    const dims = info?.w && info?.h ? clampDimensions(info.w, info.h) : undefined
    return (
      <div className="relative inline-block">
        <video
          src={url}
          autoPlay
          loop
          muted
          playsInline
          className="max-w-sm max-h-80 rounded-md border border-[rgb(var(--np-border))]"
          style={dims}
        />
        <span className="absolute bottom-1 left-1 px-1 py-0.5 rounded bg-black/60 text-white text-[9px] font-data leading-none">
          GIF
        </span>
      </div>
    )
  }

  return (
    <video
      src={url}
      controls
      poster={poster ?? undefined}
      className="max-w-sm max-h-80 rounded-md border border-[rgb(var(--np-border))]"
      preload="metadata"
    />
  )
}

function StickerMessage({ event }: MediaMessageProps) {
  const { url, loading } = useMediaUrl(event)
  const info = event.content.info as { w?: number; h?: number } | undefined
  const alt = (event.content.body as string) ?? 'Sticker'
  const dims = info?.w && info?.h ? clampDimensions(info.w, info.h, 160, 160) : { width: 128, height: 128 }

  if (loading) {
    return (
      <div className="flex items-center justify-center" style={dims}>
        <span className="text-xs text-[rgb(var(--np-tx-muted))]">Decrypting...</span>
      </div>
    )
  }
  if (!url) return <p className="text-sm text-[rgb(var(--np-tx-muted))] italic">Sticker unavailable</p>

  return <img src={url} alt={alt} className="object-contain" style={dims} />
}

function AudioMessage({ event }: MediaMessageProps) {
  const { url, loading } = useMediaUrl(event)

  if (loading) return <p className="text-sm text-[rgb(var(--np-tx-muted))] italic">Decrypting audio...</p>
  if (!url) return <p className="text-sm text-[rgb(var(--np-tx-muted))] italic">Audio unavailable</p>

  return (
    <div className="flex items-center gap-3 bg-[rgb(var(--np-raised))] rounded-md px-3 py-2 max-w-sm">
      <svg className="shrink-0 text-[rgb(var(--np-accent))]" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M9 18V5l12-2v13" />
        <circle cx="6" cy="18" r="3" />
        <circle cx="18" cy="16" r="3" />
      </svg>
      <audio src={url} controls className="flex-1 h-8" preload="metadata" />
    </div>
  )
}

function FileMessage({ event }: MediaMessageProps) {
  const { url, loading } = useMediaUrl(event)
  const info = event.content.info as { size?: number; mimetype?: string } | undefined
  const filename = (event.content.body as string) ?? 'File'

  const sizeStr = info?.size ? formatFileSize(info.size) : ''

  return (
    <a
      href={url ?? '#'}
      target="_blank"
      rel="noopener noreferrer"
      download={filename}
      className="flex items-center gap-3 bg-[rgb(var(--np-raised))] hover:bg-[rgb(var(--np-overlay))] border border-[rgb(var(--np-border))] rounded-md px-3 py-2.5 max-w-sm transition-colors"
    >
      <svg className="shrink-0 text-[rgb(var(--np-accent))]" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
      </svg>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-[rgb(var(--np-tx-primary))] truncate">{filename}</p>
        <p className="text-[10px] font-data text-[rgb(var(--np-tx-muted))]">
          {loading ? 'Decrypting...' : [info?.mimetype, sizeStr].filter(Boolean).join(' · ')}
        </p>
      </div>
      <svg className="shrink-0 text-[rgb(var(--np-tx-muted))]" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>
    </a>
  )
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function LocationMessage({ event }: MediaMessageProps) {
  const coords = extractLocation(event.content)
  const [open, setOpen] = useState(false)
  if (!coords) return <p className="text-sm text-[rgb(var(--np-tx-muted))] italic">📍 Location</p>

  const staticMapUrl = `/api/static-map?lat=${coords.lat}&lng=${coords.lng}&zoom=15`
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="block w-72 max-w-full rounded-md border border-[rgb(var(--np-border))] overflow-hidden bg-[rgb(var(--np-raised))] cursor-pointer hover:opacity-90 transition-opacity text-left"
      >
        <img src={staticMapUrl} alt="Location" className="w-full h-40 object-cover" />
        <div className="flex items-center gap-1.5 px-2.5 py-1.5">
          <svg className="shrink-0 text-[rgb(var(--np-accent))]" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          <span className="text-xs text-[rgb(var(--np-tx-secondary))] truncate">
            {coords.label || 'Shared location'}
          </span>
        </div>
      </button>
      {open && <MapDialog coords={coords} onClose={() => setOpen(false)} />}
    </>
  )
}

export function MediaMessage({ event }: MediaMessageProps) {
  if (event.type === 'm.sticker') return <StickerMessage event={event} />

  const msgtype = event.content.msgtype as string

  switch (msgtype) {
    case 'm.location':
      return <LocationMessage event={event} />
    case 'm.image':
      return <ImageMessage event={event} />
    case 'm.video':
      return <VideoMessage event={event} />
    case 'm.audio':
      return <AudioMessage event={event} />
    case 'm.file':
      return <FileMessage event={event} />
    default:
      return null
  }
}
